// MeetingPrep (TASK-496) — ported from SixEase widgets/MeetingPrep.tsx, re-grounded on
// Kielis getClientDna (+ getPortfolioOverview for the client name, which Kielis DnaResponse
// lacks) and re-tokenised to shadcn tokens. The agenda is generated from the client's actual
// DNA (life events, open promises, values/red-lines) — items only appear when the data exists;
// nothing is fabricated. Key Facts come straight from the DNA profile.
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { FileText, Loader2 } from 'lucide-react';
import {
  getOpeners,
  type DnaResponse,
  type DnaItem,
  type MeetingOpener,
} from '../../../services/knowledgeGraphService';
import { portfolioApiService } from '../../../services/api/portfolioApiService';
import { httpClient } from '../../../lib/httpClient';
import { meetingApi, type MeetingDetails } from '../../../services/api/meetingApi';
import { useFocusStore } from '../../../stores/focusStore';
import { MEETING_TYPE_LABELS, STATUS_STYLES } from '../../meeting/meetingRouting';
import { useClientDna } from '../../../hooks/useClientDna';
import { documentService, type ClientDocument } from '../../../services/documentService';
import { WidgetContainer } from '../WidgetContainer';
import { useCanvasActions } from '../../../stores/canvasStore';

/** TASK-670 — `GET /meeting/clients/{id}/current-meeting`. The two concepts are
 *  deliberately separate: `upcoming_session` is what to PREPARE for, `latest_brief_session`
 *  is the most recent session that actually HAS a brief. They are frequently different. */
interface MeetingSessionRef {
  session_id: string;
  status: string;
  meeting_type: string;
  scheduled_at: string | null;
  has_brief: boolean;
}

interface CurrentMeetingResponse {
  client_id: string;
  upcoming_session: MeetingSessionRef | null;
  latest_brief_session: MeetingSessionRef | null;
  session_id: string | null;
}

interface AgendaItem {
  title: string;
  detail: string;
}

// TASK-715: the compliance badge moved with the brief content into the preparation
// workspace (it required fetching the brief — the parallel implementation this
// ticket deleted). The glance shows brief STATUS; outcomes live in the workspace.
function texts(items: DnaItem[] | null | undefined): string[] {
  return (items ?? []).map((i) => i.text).filter(Boolean);
}

function buildAgenda(dna: DnaResponse): AgendaItem[] {
  const agenda: AgendaItem[] = [];
  const life = texts(dna.life_events);
  const promises = texts(dna.promises);
  const values = texts(dna.values);
  const exclusions = texts(dna.exclusions);

  if (life.length) {
    agenda.push({ title: 'Life Event Check-in', detail: life.slice(0, 2).join('; ') });
  }
  if (promises.length) {
    agenda.push({ title: 'Open Promises', detail: promises.slice(0, 3).join('; ') });
  }
  if (values.length || exclusions.length) {
    const parts = [
      values.length ? `Reaffirm priorities: ${values.slice(0, 3).join(', ')}` : '',
      exclusions.length ? `Confirm red lines: ${exclusions.slice(0, 2).join(', ')}` : '',
    ].filter(Boolean);
    agenda.push({ title: 'Values & Portfolio Alignment', detail: parts.join('. ') });
  }
  return agenda;
}

/** The client's real documents (data-ingestion) that back this meeting's brief — the visible
 * proof of the closed loop (TASK-577). Reuses documentService; renders nothing when the client
 * has no documents, so nothing is fabricated. */
function SourceDocumentsPanel({ clientId }: { clientId: string }) {
  const docsQuery = useQuery({
    queryKey: ['client-documents', clientId],
    queryFn: () => documentService.getClientDocuments(clientId),
    enabled: !!clientId,
    staleTime: 60 * 1000,
  });
  const docs = docsQuery.data?.documents ?? [];
  if (docsQuery.isLoading) return <div className="h-16 animate-pulse rounded-xl bg-accent" />;
  if (!docs.length) return null;
  return (
    <div className="rounded-xl border border-border bg-muted/40 p-3">
      <h3 className="mb-3 text-[13px] font-semibold text-foreground">Source Documents</h3>
      <ul className="space-y-2">
        {docs.map((d: ClientDocument) => (
          <li key={d.id} className="flex items-center gap-2 text-sm">
            <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="flex-1 truncate text-foreground">{d.filename}</span>
            <span className="shrink-0 text-xs text-muted-foreground">
              {documentService.getDocTypeDisplayName(d.doc_type)}
            </span>
            <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${documentService.getStatusColor(d.status)}`}>
              {d.status}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// TASK-715: BriefPanel (the 240-line parallel brief implementation — content
// sections, compliance badge, PPTX export/preview/FinSA flow) is DELETED. The
// glance level shows brief STATUS only; the full brief lives in the preparation
// workspace (PreparationPage), opened via the ⤢ focus popup.
function BriefStatus({ sessionId, hasBrief }: { sessionId: string; hasBrief: boolean }) {
  return (
    <div
      data-testid="prep-brief-status"
      className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-muted/40 p-3"
    >
      <div className="flex items-center gap-2 text-sm">
        <FileText className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium text-foreground">Meeting Brief</span>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
            hasBrief ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
          }`}
        >
          {hasBrief ? 'Ready' : 'Not generated yet'}
        </span>
      </div>
      <button
        type="button"
        data-testid="prep-open-workspace"
        onClick={() =>
          useFocusStore.getState().push({ workspace: 'meeting', entityId: sessionId })
        }
        className="rounded-md border border-border px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:bg-accent"
      >
        Open preparation workspace ⤢
      </button>
    </div>
  );
}

/** TASK-671 — the session-identity header. Preparation is bound to ONE meeting: this
 *  renders the goal, date/time (local), type, status and capture plan of the exact session
 *  so two meetings for the same client can never be confused. */
function PrepHeader({ details }: { details: MeetingDetails }) {
  const when = details.scheduled_at ? new Date(details.scheduled_at) : null;
  const capturePlan = details.meeting_url ? 'Remote — bot' : 'In person';
  return (
    <div className="rounded-xl border border-border bg-muted/40 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-lg font-semibold tracking-tight text-foreground">
          {details.client_name || 'Client'}
        </h2>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
            STATUS_STYLES[details.status?.toLowerCase()] ?? 'bg-gray-100 text-gray-700'
          }`}
        >
          {details.status?.toLowerCase()}
        </span>
        {/* TASK-714: ⤢ opens the full prep workspace as a focus popup — the URL
            stays /dashboard; the glance tile remains behind the overlay. */}
        <button
          type="button"
          data-testid="prep-focus-open"
          title="Open full preparation workspace"
          aria-label="Open full preparation workspace"
          onClick={() =>
            useFocusStore.getState().push({ workspace: 'meeting', entityId: details.session_id })
          }
          className="ml-auto rounded-md border border-border px-1.5 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          ⤢
        </button>
      </div>
      {/* The goal is THIS meeting's identity — shown verbatim, never a generic DNA agenda. */}
      <p className="mt-1 text-sm font-medium text-foreground" data-testid="prep-goal">
        {details.meeting_goal?.trim() || 'No goal set for this meeting'}
      </p>
      <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <div>
          <dt className="inline">When: </dt>
          <dd className="inline text-foreground" data-testid="prep-when">
            {when ? format(when, 'EEE MMM d, yyyy HH:mm') : 'Not scheduled'}
          </dd>
        </div>
        <div>
          <dt className="inline">Type: </dt>
          <dd className="inline text-foreground">
            {MEETING_TYPE_LABELS[details.meeting_type] ?? details.meeting_type}
          </dd>
        </div>
        {details.duration_minutes ? (
          <div>
            <dt className="inline">Duration: </dt>
            <dd className="inline text-foreground">{details.duration_minutes} min</dd>
          </div>
        ) : null}
        <div>
          <dt className="inline">Capture: </dt>
          <dd className="inline text-foreground">{capturePlan}</dd>
        </div>
      </dl>
    </div>
  );
}

/**
 * TASK-671 — MeetingPrep is now SESSION-scoped. `sessionId` is the primary identity;
 * `clientId` is only supporting context (DNA agenda). When summoned from a meeting row we
 * have the session directly. When summoned from a client surface (ClientBook) we resolve
 * the meeting to prepare for and then load THAT session's own detail — the brief is never
 * substituted from a different meeting for the same client.
 */
export function MeetingPrep({
  sessionId: sessionIdProp,
  clientId: clientIdProp,
  createdNow = false,
}: {
  sessionId?: string;
  clientId?: string;
  /** Creation handoff: explain what exists and what the RM still needs to do. */
  createdNow?: boolean;
}) {
  const { addSpecs } = useCanvasActions();

  // Client-scoped fallback: resolve the client's meeting only when no session was passed.
  // TASK-670's endpoint returns the upcoming meeting and the latest one with a brief.
  const currentMeetingQuery = useQuery({
    queryKey: ['current-meeting', clientIdProp],
    queryFn: () =>
      httpClient.get<CurrentMeetingResponse>(`/meeting/clients/${clientIdProp}/current-meeting`),
    enabled: !sessionIdProp && !!clientIdProp,
    staleTime: 60 * 1000,
  });

  const latestBrief = currentMeetingQuery.data?.latest_brief_session ?? null;
  const upcoming = currentMeetingQuery.data?.upcoming_session ?? null;
  // Identity: an explicit session always wins. Otherwise prefer the upcoming meeting (what
  // to prepare for), falling back to the most recent one that has a brief.
  const sessionId =
    sessionIdProp ?? upcoming?.session_id ?? latestBrief?.session_id ?? undefined;

  // The session's own detail — the header identity AND the effective client for DNA.
  const detailsQuery = useQuery({
    queryKey: ['meeting-details', sessionId],
    queryFn: () => meetingApi.getMeetingDetails(sessionId!),
    enabled: !!sessionId,
    staleTime: 60 * 1000,
  });
  const details = detailsQuery.data ?? null;
  const clientId = details?.client_id ?? clientIdProp ?? '';

  // DNA is required (gates error); overview only supplies the client name and degrades to ''.
  const dnaQuery = useClientDna(clientId);
  // TASK-689 (review fix): cited openers. Server-side (`build_openers`) rather than derived
  // here — an opener is a citation, and re-deriving it in TS would be a second implementation
  // of the rule that says an uncited claim is not usable.
  const openersQuery = useQuery({
    queryKey: ['meeting-openers', clientId],
    queryFn: () => getOpeners(clientId),
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000,
  });
  const openers: MeetingOpener[] = openersQuery.data?.openers ?? [];
  const ovQuery = useQuery({
    queryKey: ['portfolio-overview', clientId],
    queryFn: () => portfolioApiService.getPortfolioOverview(clientId),
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000,
  });

  // In client-scoped mode only: if the brief we would show belongs to an earlier meeting
  // than the one being prepared, say so — it is never silently presented as this meeting's.
  const briefIsHistorical = Boolean(
    !sessionIdProp && latestBrief && (!upcoming || latestBrief.session_id !== sessionId),
  );

  const meetingQuery = detailsQuery; // header/brief gating reads the session detail now
  const loading = dnaQuery.isLoading || ovQuery.isLoading;
  const dna = dnaQuery.data ?? null;

  return (
    <WidgetContainer title="Meeting Prep" source="Knowledge Graph">
      {details && <PrepHeader details={details} />}
      {details &&
        (createdNow ||
          details.has_brief === false ||
          details.preparation_status === 'not_started') && (
        <div
          role="status"
          data-testid="meeting-created-handoff"
          className="rounded-xl border border-kielis-blue/30 bg-kielis-blue/5 p-4"
        >
          <p className="font-medium text-foreground">
            {createdNow ? 'Meeting created successfully' : 'Meeting ready to prepare'}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            The meeting is scheduled and saved. Its brief has not been generated yet—prepare it
            now, or return from the Meetings list later.
          </p>
        </div>
      )}

      {/* The meeting-bound brief is the primary preparation job. It must not wait for the
          reusable client DNA request, otherwise a slow/failing DNA request makes the brief
          appear to be missing. */}
      {currentMeetingQuery.isLoading || meetingQuery.isLoading ? (
        <div
          role="status"
          aria-live="polite"
          className="flex items-center gap-2 rounded-xl border border-border bg-muted/30 p-4 text-sm text-muted-foreground"
        >
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading this meeting&apos;s brief…
        </div>
      ) : meetingQuery.isError ? (
        <div
          role="status"
          className="flex flex-wrap items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800"
        >
          <span>The meeting brief could not be loaded.</span>
          <button
            type="button"
            onClick={() => meetingQuery.refetch()}
            className="font-medium underline hover:no-underline"
          >
            Try again
          </button>
        </div>
      ) : sessionId ? (
        <>
          {briefIsHistorical && (
            <p
              className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
              role="note"
            >
              {upcoming
                ? 'No brief has been prepared for the upcoming meeting yet. Showing the most recent brief from an earlier meeting.'
                : 'No meeting is scheduled. Showing the most recent brief from an earlier meeting.'}
            </p>
          )}
          <BriefStatus sessionId={sessionId} hasBrief={details?.has_brief ?? Boolean(latestBrief)} />
        </>
      ) : !currentMeetingQuery.isLoading ? (
        <div className="rounded-xl border border-border bg-muted/30 p-4 text-sm">
          <p className="font-medium text-foreground">No meeting scheduled</p>
          <p className="mt-1 text-muted-foreground">
            This client has no meeting yet, so there is no brief to prepare. Create a meeting to
            generate one.
          </p>
          <button
            type="button"
            onClick={() => addSpecs([{ component: 'MeetingsList', props: {} }])}
            className="mt-3 inline-block font-medium text-kielis-navy underline hover:no-underline"
          >
            Create a meeting
          </button>
        </div>
      ) : null}

      {dnaQuery.isError && (
        <p className="text-[13px] text-muted-foreground">
          The reusable client context could not be loaded. {dnaQuery.error instanceof Error ? dnaQuery.error.message : String(dnaQuery.error)}
        </p>
      )}
      {!dnaQuery.isError && loading && <div className="h-48 animate-pulse rounded-xl bg-accent" />}
      {!dnaQuery.isError &&
        !loading &&
        dna &&
        (() => {
          const agenda = buildAgenda(dna);
          const redLines = texts(dna.exclusions);
          return (
            <div className="flex flex-col gap-5">
              {/* TASK-671: the DNA agenda is SUPPORTING client context, not the meeting's
                  identity. Labelled as such so it is never mistaken for this meeting's goal
                  (which lives in the header) — the review found identical DNA agendas made
                  every meeting for a client look the same. */}
              <div className="flex flex-col gap-4 lg:flex-row">
                <div className="flex-1 rounded-xl border border-border bg-muted/40 p-3">
                  <h3 className="mb-3 text-[13px] font-semibold text-foreground">
                    Reusable client context <span className="font-normal text-muted-foreground">(shared across this client&apos;s meetings · from DNA — {dna.mandate ?? '—'} mandate)</span>
                  </h3>
                  {agenda.length ? (
                    <ul className="space-y-3">
                      {agenda.map((item, i) => (
                        <li key={item.title} className="flex items-start gap-3">
                          <span className="mt-0.5 font-mono text-primary">{String(i + 1).padStart(2, '0')}</span>
                          <div>
                            <h4 className="text-sm font-medium text-foreground">{item.title}</h4>
                            <p className="mt-0.5 text-sm text-muted-foreground">{item.detail}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Not enough DNA captured yet to suggest an agenda for this client.
                    </p>
                  )}

                  {/* TASK-689 (review fix): openers were built by `build_openers` and then
                      thrown away — nothing called the skill and this widget builds its own
                      agenda client-side. Each is a sentence the client actually said, with
                      its locator; an uncited claim never becomes an opener, so an empty list
                      renders nothing rather than a generic ice-breaker. */}
                  {openers.length > 0 && (
                    <div className="mt-4 border-t border-border pt-3" data-testid="meeting-openers">
                      <h4 className="mb-2 text-[12px] font-semibold text-foreground">
                        Open with their own words
                      </h4>
                      <ul className="space-y-2">
                        {openers.map((opener) => (
                          <li key={opener.claim_id ?? opener.excerpt} data-testid="meeting-opener">
                            <p className="text-sm text-foreground">
                              {opener.lead}
                              {opener.subject ? ` ${opener.subject}` : ''}…
                            </p>
                            <p className="mt-0.5 border-l-2 border-border pl-2 text-xs italic text-muted-foreground">
                              “{opener.excerpt}”
                              {opener.observation_count && opener.observation_count > 1 && (
                                <span className="ml-1 not-italic text-[10px]">
                                  said {opener.observation_count}×
                                </span>
                              )}
                            </p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div className="w-full shrink-0 lg:w-[280px]">
                  <div className="rounded-xl border border-border bg-muted/40 p-3">
                    <h3 className="mb-3 text-[13px] font-semibold text-foreground">Key Facts</h3>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-muted-foreground">Mandate: </span>
                        <span className="text-foreground">{dna.mandate ?? '—'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Temperament: </span>
                        <span className="text-foreground">{dna.temperament || '—'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Red Lines: </span>
                        <span className="text-foreground">{redLines.length ? redLines.join(', ') : '—'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <SourceDocumentsPanel clientId={clientId} />
            </div>
          );
        })()}
    </WidgetContainer>
  );
}
